import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Backdrop,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
} from "@mui/material";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import ModeEditOutlineOutlined from "@mui/icons-material/ModeEditOutlineOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Swal from "sweetalert2";
import { GetStationDetailById, GetFinalStationList } from "api/stations";
import { GetSumPackagesByStation, GetPackageList, PatchPackageDelivery } from "api/packages";
import { StoreTrackingLog } from "api/trackingLogs";
import { DataGridCustomToolbarClientMode } from "components/DataGridCustomToolbar";
import { copyTextToClipboard, playCorrectSound } from "utils";
import { user } from "localStorage";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { isMobile } from "react-device-detect";
import BentoCard from "components/bento/BentoCard";
import StyledDataGrid from "components/bento/StyledDataGrid";
import EmptyState from "components/bento/EmptyState";
import PackageEditDialog from "components/PackageEditDialog";
import TrackingDetailDialog from "components/TrackingDetialDialog.";

const PackageListByStation = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedStation, setSelectedStation] = useState(null);
  const [rows, setRows] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [stationList, setStationList] = useState([]);
  const [filteredRows, setFilteredRows] = useState(null);

  // values to be sent to the backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");

  // Drill-down dialog: parcels for one customer at the selected station.
  const [viewCustomer, setViewCustomer] = useState(null); // { id, name }
  const [viewRows, setViewRows] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  // Edit-package dialog, reused from packageList.jsx's own edit button.
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [packageData, setPackageData] = useState(null);

  // Tracking-log dialog, reused from packageList.jsx's tracking column.
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");

  // "Move to end" needs the actual END station's id/name/message (mirrors
  // SelfPickup.jsx's handleSuccessButtonClicked) — fetched once on mount.
  const [endStation, setEndStation] = useState(null);
  useEffect(() => {
    GetStationDetailById("END").then((res) => {
      if (res && res.success) setEndStation(res.data);
    });
  }, []);

  const _user = user();

  function openCustomerDrillDown(customer) {
    setViewCustomer(customer);
    setViewRows([]);
    setViewLoading(true);
    // Same membership rule the Stock aggregate itself uses (whatsappStatus:true
    // at this station) so the count here always matches the Stock card total.
    GetPackageList(0, 200, {}, "", { customerId: customer._id, station: selectedStation?.id }).then((res) => {
      if (res.status === 200) {
        setViewRows((res.data || []).filter((p) => p.whatsappStatus === true));
      }
      setViewLoading(false);
    });
  }

  function buttonEditHandler(rowValue) {
    setPackageData(rowValue);
    setEditDialogOpen(true);
  }
  function handleEditDialogClose() {
    setEditDialogOpen(false);
    setPackageData(null);
  }
  function handleEditSuccess() {
    // Re-fetch from the server rather than patch viewRows in place — keeps the
    // whatsappStatus:true filter (and every server-computed field) authoritative.
    if (viewCustomer) openCustomerDrillDown(viewCustomer);
  }

  function handleMoveToEnd(rowValue) {
    Swal.fire({
      icon: "question",
      title: rowValue.tracking,
      text: "Move this package to the end station?",
      showConfirmButton: true,
      confirmButtonText: "Yes",
      showDenyButton: true,
    }).then((result) => {
      if (!result.isConfirmed) return;
      const target = endStation || { id: "END", name: "END", message: "Delivered", parcelStatus: "Delivered" };
      PatchPackageDelivery(rowValue._id, target.id, target.parcelStatus || "Delivered", true, true, "Manual").then((res) => {
        if (res && res.success) {
          StoreTrackingLog(
            rowValue._id,
            rowValue.tracking,
            target.name,
            target.message || "Delivered",
            "Manually moved to end station from Stock view — not the normal pickup/delivery process.",
            _user?.name || "system"
          );
          playCorrectSound();
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `Tracking no. ${rowValue.tracking} has been updated`,
            timer: 1500,
          });
          if (viewCustomer) openCustomerDrillDown(viewCustomer);
          if (selectedStation) RefreshPackagesList(selectedStation.id);
        } else {
          Swal.fire({ icon: "error", title: "Failed", text: (res && res.message) || "Could not update the package" });
        }
      });
    });
  }


  function RefreshPackagesList(stationId) {
    setRows([]);
    setIsLoading(true);
    GetSumPackagesByStation(stationId).then((res) => {
      if (res.status === 200) {
        setRows(res.data);
      }
      setIsLoading(false);
    });
  }

  useEffect(() => {
    if (selectedStation) {
      RefreshPackagesList(selectedStation.id);
    }
  }, [selectedStation]);

  useEffect(() => {
    GetFinalStationList().then((res) => {
      if (res && res.success && Array.isArray(res.data)) {
        setStationList(res.data);
      }
      // Intentionally do NOT auto-select a station here — the Stock tab waits
      // for the user to pick a station before loading any data.
    });
  }, []);

  useEffect(() => {
    filterPackage(rows, search);
  }, [rows, search]);

  function filterPackage(dataArr, searchValue) {
    if (dataArr) {
      let newArr = dataArr.slice();
      if (searchValue) {
        newArr = newArr.filter((element) => {
          return (
            (element._id.customer &&
              element._id.customer.name
                .toLowerCase()
                .includes(searchValue.toLowerCase())) ||
            (element._id.customer &&
              element._id.customer.whatsapp
                .toLowerCase()
                .includes(searchValue.toLowerCase()))
          );
        });
      }
      setFilteredRows(newArr);
    }
  }

  const columns = [
    {
      field: "_id",
      headerName: "ID",
      flex: 1,
      valueGetter: (params) => params.row._id.customer._id,
      hide: true,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 0.7,
      valueGetter: (params) => params.row._id.customer.name,
    },
    {
      field: "whatsapp",
      headerName: "Whatsapp",
      flex: 1,
      valueGetter: (params) => params.row._id.customer.whatsapp,
    },

    {
      field: "smsMessage",
      headerName: "Message",
      flex: 3,
      hide: isMobile,
    },
    {
      field: "totalPackages",
      headerName: "item(s).",
      flex: 0.5,
    },
    {
      field: "totalShippingFee",
      headerName: "Fee",
      flex: 0.5,
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      flex: 0.5,
    },
    {
      field: "copy",
      headerName: "Copy",
      flex: 0.5,
      hide: isMobile,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outlined"
            onClick={() => {
              copyTextToClipboard(cellValues.row.smsMessage);
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <ContentCopyIcon />
          </IconButton>
        );
      },
    },
    {
      field: "view",
      headerName: "View",
      flex: 0.4,
      renderCell: (cellValues) => {
        const customer = cellValues.row._id.customer;
        return (
          <IconButton
            onClick={() => openCustomerDrillDown(customer)}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <VisibilityIcon />
          </IconButton>
        );
      },
    },
  ];

  const viewColumns = [
    {
      field: "tracking",
      headerName: "Tracking",
      flex: 1,
      renderCell: (cellValues) => (
        <Link
          component="button"
          color="secondary"
          underline="hover"
          onClick={() => {
            setTrackingNumber(cellValues.row.tracking);
            setTrackingDialogOpen(true);
          }}
        >
          {cellValues.row.tracking}
        </Link>
      ),
    },
    { field: "description", headerName: "Description", flex: 1 },
    { field: "status", headerName: "Status", flex: 0.6 },
    { field: "shippingFee", headerName: "Fee", flex: 0.4 },
    { field: "amount", headerName: "COD", flex: 0.4 },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 0.8,
      valueGetter: (params) => moment(params.row.updatedAt).format("DD/MM/YYYY, h:mm A"),
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 0.4,
      sortable: false,
      renderCell: (cellValues) => (
        <IconButton
          onClick={() => buttonEditHandler(cellValues.row)}
          sx={{ "&:hover": { color: theme.palette.warning.main } }}
        >
          <ModeEditOutlineOutlined />
        </IconButton>
      ),
    },
    {
      field: "moveToEnd",
      headerName: "Move to End",
      flex: 0.5,
      sortable: false,
      renderCell: (cellValues) => (
        <IconButton
          aria-label="Move to end station"
          onClick={() => handleMoveToEnd(cellValues.row)}
          sx={{ "&:hover": { color: theme.palette.warning.main } }}
        >
          <CheckIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box marginTop={1}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        <FormControl sx={{ minWidth: 260 }}>
          <InputLabel>Select station</InputLabel>
          <Select
            label="Select station"
            value={selectedStation ? selectedStation.id : ""}
            onChange={(e) => {
              GetStationDetailById(e.target.value).then((res) => {
                if (res && res.success) {
                  setSelectedStation(res.data);
                }
              });
            }}
            required
          >
            {stationList &&
              stationList.map(({ id, name }) => {
                return (
                  <MenuItem key={id} value={id}>
                    {id} - {name}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </BentoCard>

      {/*  Data Grid  */}
      <Box height="73vh" width="100%">
        {!selectedStation ? (
          <BentoCard sx={{ height: "100%" }} bodySx={{ height: "100%" }}>
            <Box height="100%" display="flex" alignItems="center" justifyContent="center">
              <EmptyState
                icon={<WarehouseIcon sx={{ fontSize: 64, opacity: 0.4 }} />}
                message={
                  <>
                    <Typography variant="h5" color="secondary">
                      Select a station to view stock
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pick a station from the dropdown above to load its packages.
                    </Typography>
                  </>
                }
              />
            </Box>
          </BentoCard>
        ) : (
          <StyledDataGrid
            loading={isLoading}
            getRowId={(row) => row._id.customer._id}
            rows={filteredRows || []}
            columns={columns}
            rowCount={filteredRows ? filteredRows.length : 0}
            rowsPerPageOptions={[20, 50, 100]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode="client"
            sortingMode="client"
            filterMode="client"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            // onSortModelChange={(newSortModel) => setSort(...newSortModel)}
            components={{ Toolbar: DataGridCustomToolbarClientMode }}
            componentsProps={{
              toolbar: { search, setSearch },
            }}
            containerSx={{ height: "100%" }}
            autoHeight={false}
          />
        )}
      </Box>

      <Dialog open={!!viewCustomer} onClose={() => setViewCustomer(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {viewCustomer?.name}
          <Typography variant="body2" color="text.secondary">
            {selectedStation ? `${selectedStation.id} - ${selectedStation.name}` : ""}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ height: "60vh" }}>
          <StyledDataGrid
            loading={viewLoading}
            getRowId={(row) => row._id}
            rows={viewRows}
            columns={viewColumns}
            hideFooter
            containerSx={{ height: "100%" }}
            autoHeight={false}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setViewCustomer(null)}>
            {t("common.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <PackageEditDialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        packageData={packageData}
        onSuccess={handleEditSuccess}
      />
      <TrackingDetailDialog
        open={trackingDialogOpen}
        onClose={() => setTrackingDialogOpen(false)}
        trackingNumber={trackingNumber}
      />
    </Box>
  );
};

export default PackageListByStation;
